import { createRef, useState, useImperativeHandle, forwardRef } from "react";
import ReactDOM from "react-dom/client";
import { XAdkSDKProps, XAdkProviderHandle, IMessage } from "@/types";
import XAdkProvider from "@/components/XAdkProvider";
import { SendContent } from "@/types";
import * as protocol from "@/core/protocol";
import { createAdkConversationController } from "@/core/runtime";
import { createXGroupOpenApiTransport } from "@/presets/xgroup";

/**
 * 内部包装组件：管理 token 状态，支持运行时动态更新
 */
interface WrapperHandle extends XAdkProviderHandle {
  setToken: (token: string) => void;
}

interface WrapperProps extends Omit<XAdkSDKProps, "children"> {
  showSessionList: boolean;
  allowUpload: boolean;
  draggable: boolean;
  clearBtnShow: boolean;
}

const SDKWrapper = forwardRef<WrapperHandle, WrapperProps>(
  (
    {
      token: initialToken,
      showSessionList,
      allowUpload,
      draggable,
      clearBtnShow,
      ...rest
    },
    ref,
  ) => {
    const [token, setToken] = useState(initialToken);
    const providerRef = createRef<XAdkProviderHandle>();

    useImperativeHandle(
      ref,
      () => ({
        setToken,
        startChat: (content: SendContent) =>
          providerRef.current?.startChat(content),
        stopChat: () => providerRef.current?.stopChat(),
        reChat: () => providerRef.current?.reChat(),
        clearChat: () => providerRef.current?.clearChat(),
        deleteSession: (id: string) =>
          providerRef.current?.deleteSession(id),
        updateSession: (id: string, title: string) =>
          providerRef.current?.updateSession(id, title),
        getAppInfo: () => providerRef.current?.getAppInfo(),
        getMessages: () => providerRef.current?.getMessages() ?? [],
        getSessions: () => providerRef.current?.getSessions() ?? [],
        setMessages: (msgs: IMessage[]) =>
          providerRef.current?.setMessages(msgs),
        setCurrentSession: (id: string) =>
          providerRef.current?.setCurrentSession(id),
        setStateDelta: (delta: Record<string, any> | undefined) =>
          providerRef.current?.setStateDelta(delta),
      }),
      [providerRef],
    );

    return (
      <XAdkProvider ref={providerRef} {...rest} token={token}>
        <XAdkProvider.DefaultLayout
          showSessionList={showSessionList}
          allowUpload={allowUpload}
          draggable={draggable}
          clearBtnShow={clearBtnShow}
          showFnCallDetail={true}
        />
      </XAdkProvider>
    );
  },
);

SDKWrapper.displayName = "SDKWrapper";

/**
 * XAiWebSDK - UMD 入口类
 *
 * 用于通过 <script> 标签引入 SDK 的场景
 *
 * @example
 * ```html
 * <script src="chat-sdk.min.js"></script>
 * <script>
 *   const sdk = XAiWebSDK.initChatbot({
 *     url: 'https://api.example.com',
 *     token: 'your-token',
 *     config: {
 *       appNo: 'your-app-no',
 *       session: {
 *         showSessionList: true
 *       }
 *     }
 *   });
 *
 *   // token 过期后动态更新
 *   sdk.setToken('new-token');
 * </script>
 * ```
 */
class XAiWebSDK {
  /** UMD 场景下暴露协议工具，方便无构建环境复用 ADK event/message 转换能力。 */
  static protocol = protocol;

  /** UMD 场景下创建 headless 对话控制器，不强制渲染默认聊天模板。 */
  static createAdkController = createAdkConversationController;

  /** UMD 场景下创建众安信科默认 openApi transport。 */
  static createXGroupOpenApiTransport = createXGroupOpenApiTransport;

  container: HTMLElement;

  props: XAdkSDKProps;

  root: any;

  wrapperRef = createRef<WrapperHandle>();

  // ———— hook ————
  onError?: (error: any) => void;

  onSuccess?: (appInfo: any) => void;

  onMessage?: (content: string, data: IMessage) => void;

  constructor(container: HTMLElement, props: XAdkSDKProps) {
    this.container = container;
    this.props = props;
    this.onError = props.onError;
    this.onSuccess = props.onSuccess;
    this.onMessage = props.onMessage;
    this.mount();
  }

  mount() {
    // 提取配置
    const { config = {} } = this.props;
    const showSessionList = config.session?.showSessionList ?? false;
    const allowUpload = config.allowUpload ?? false;
    const draggable = config.draggable ?? true;
    const clearBtnShow = config.chatProps?.clearBtnShow ?? true;

    const element = (
      <div style={{ width: "100%", height: "100%" }}>
        <SDKWrapper
          ref={this.wrapperRef}
          {...this.props}
          showSessionList={showSessionList}
          allowUpload={allowUpload}
          draggable={draggable}
          clearBtnShow={clearBtnShow}
        />
      </div>
    );

    if (ReactDOM.createRoot) {
      this.root = ReactDOM.createRoot(this.container);
      this.root.render(element);
    } else {
      // 兼容 React 17
      // @ts-ignore
      ReactDOM.render(element, this.container);
    }
  }

  unmount() {
    if (this.root && this.root.unmount) {
      this.root.unmount();
    }
  }

  /**
   * 初始化聊天机器人(推荐方式)
   *
   * @param props - SDK 配置参数
   * @returns XAiWebSDK 实例
   *
   * @example
   * ```javascript
   * const sdk = XAiWebSDK.initChatbot({
   *   url: 'https://api.example.com',
   *   token: 'your-token',
   *   config: {
   *     appNo: 'your-app-no',
   *     session: {
   *       showSessionList: true  // 显示会话列表
   *     },
   *     allowUpload: true,       // 允许文件上传
   *     chatProps: {
   *       clearBtnShow: false    // 隐藏清空按钮
   *     }
   *   },
   *   componentProps: {
   *     id: 'my-chatbot',        // 容器 ID
   *     width: '100%',
   *     height: '600px'
   *   }
   * });
   * ```
   */
  static initChatbot(props: XAdkSDKProps): XAiWebSDK {
    let container: HTMLElement;

    // 检查是否有指定的 componentProps.id
    if (props.componentProps?.id) {
      const existingContainer = document.getElementById(
        props.componentProps.id,
      );
      if (existingContainer) {
        container = existingContainer;
      } else {
        // 如果指定的 ID 不存在，创建新元素
        container = document.createElement("div");
        container.id = props.componentProps.id;
      }
    } else {
      // 没有指定 ID，创建默认容器
      container = document.createElement("div");
      container.id = "xai-chatbot-container";
    }

    // 设置容器样式 - 将除了 id 以外的所有属性都应用到容器
    if (props.componentProps) {
      const { id, ...styleProps } = props.componentProps;
      Object.assign(container.style, styleProps);
    }

    // 如果容器不在 DOM 中，添加到 body
    if (!container.parentNode) {
      document.body.appendChild(container);
    }

    return new XAiWebSDK(container, props);
  }

  /**
   * 兼容旧版本的静态方法
   * @deprecated 使用 initChatbot 替代
   */
  static create(props: XAdkSDKProps): XAiWebSDK {
    return this.initChatbot(props);
  }

  // ————对外暴露的方法————

  /**
   * 动态更新认证 token
   *
   * 用于 token 过期后刷新，更新后后续的 API 请求将使用新 token，无需重新初始化 SDK。
   *
   * @param token - 新的认证 token
   *
   * @example
   * ```javascript
   * // 监听到 token 过期后
   * const newToken = await refreshToken();
   * sdk.setToken(newToken);
   * ```
   */
  setToken(token: string) {
    this.wrapperRef.current?.setToken(token);
  }

  /**
   * 获取当前数据（聚合方法）
   * @returns 应用信息、消息列表、会话列表
   */
  getData() {
    return {
      appInfo: this.wrapperRef.current?.getAppInfo(),
      messages: this.wrapperRef.current?.getMessages(),
      sessions: this.wrapperRef.current?.getSessions(),
    };
  }

  /**
   * 获取应用信息
   * @returns 应用信息
   */
  getAppInfo() {
    return this.wrapperRef.current?.getAppInfo();
  }

  /**
   * 获取当前会话的消息列表
   * @returns 消息列表
   */
  getMessages() {
    return this.wrapperRef.current?.getMessages();
  }

  /**
   * 获取会话列表
   * @returns 会话列表
   */
  getSessions() {
    return this.wrapperRef.current?.getSessions();
  }

  /**
   * 设置消息列表
   * @param msgs - 要设置的消息数组
   */
  setMessages(msgs: IMessage[]) {
    this.wrapperRef.current?.setMessages(msgs);
  }

  /**
   * 发送消息
   * @param content - 消息内容
   *
   * @example
   * ```javascript
   * sdk.startChat({ text: 'Hello!' });
   * ```
   */
  startChat(content: SendContent) {
    this.wrapperRef.current?.startChat(content);
  }

  /**
   * 停止当前消息生成
   */
  stopChat() {
    this.wrapperRef.current?.stopChat();
  }

  /**
   * 重新生成最后一条回复
   */
  reChat() {
    this.wrapperRef.current?.reChat();
  }

  /**
   * 清空当前会话
   */
  clearChat() {
    this.wrapperRef.current?.clearChat();
  }

  /**
   * 切换到指定会话
   * @param sessionId - 会话 ID
   */
  setCurrentSession(sessionId: string) {
    this.wrapperRef.current?.setCurrentSession(sessionId);
  }

  /**
   * 删除指定会话
   * @param sessionId - 会话 ID
   */
  deleteSession(sessionId: string) {
    this.wrapperRef.current?.deleteSession(sessionId);
  }

  /**
   * 更新会话标题
   * @param sessionId - 会话 ID
   * @param title - 新标题
   */
  updateSession(sessionId: string, title: string) {
    this.wrapperRef.current?.updateSession(sessionId, title);
  }

  /**
   * 设置自定义业务参数 (stateDelta)
   * 设置后每次发送消息时会自动携带至后端
   * @param delta - 自定义参数，传入 undefined 可清除
   */
  setStateDelta(delta: Record<string, any> | undefined) {
    this.wrapperRef.current?.setStateDelta(delta);
  }
}

// 兼容 window 方式，也支持 new
// @ts-ignore
window.XAiWebSDK = XAiWebSDK;

// 支持 ESModule/UMD 直接 import
export default XAiWebSDK;
